import { useEffect, useRef } from 'react';
import { useRouter } from 'next/router';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { ApprovalFilterType } from '@/types/api';
import {
  APPROVAL_FILTER_QUERY_KEY,
  parseApprovalFilterSlug,
} from '@/utils/approval/approvalFilterLink';

/**
 * `?approvalFilter=<slug>` 로 들어오면 좌측 필터를 한 번 적용하고 파라미터를 URL 에서 걷어낸다.
 *
 * - 메일·푸시에서 바로 들어오는 경로라 `favoriteId` 와 달리 운영에서도 동작한다
 *   (favoriteId 는 dev/local 전용 가드가 있다).
 * - 적용은 파라미터가 실린 한 번에 1회. 적용 직후 파라미터를 제거하므로 사용자가 다른 필터를
 *   고른 뒤에 다시 덮어쓰는 일이 없다. `setFilter` 가 `__USER_MANUAL_SELECTION__` 을 세워 두어
 *   favoriteId 쪽 useEffect 도 이 선택을 덮지 않는다(두 파라미터가 같이 오면 approvalFilter 우선).
 * - 모르는 slug 는 필터를 건드리지 않고 파라미터만 정리해 기본 동작(전체 문서)을 유지한다.
 */
const useApprovalFilterLink = (setFilter: (id: ApprovalFilterType) => void) => {
  const router = useRouter();
  const { passportId } = useAtomValue(MyInfoState);
  const appliedRef = useRef(false);
  // setFilter 는 매 렌더 새로 만들어져 deps 에 넣으면 effect 가 계속 재실행된다.
  const setFilterRef = useRef(setFilter);
  setFilterRef.current = setFilter;

  const rawSlug = router.query[APPROVAL_FILTER_QUERY_KEY];

  useEffect(() => {
    if (!router.isReady) return;

    /**
     * 파라미터가 비면 적용 기록을 지운다.
     *
     * 예전에는 이 화면이 늘 새 탭으로 열려 매번 새로 마운트됐지만, 알림에서 같은 탭으로
     * 넘어오면(PORTAL-14273) 화면이 살아 있는 채 파라미터만 다시 붙는다. 기록을 그대로 두면
     * 두 번째 알림부터는 필터가 걸리지 않고 파라미터만 주소에 남는다.
     */
    if (rawSlug === undefined) {
      appliedRef.current = false;
      return;
    }
    if (appliedRef.current) return;

    /**
     * 실제로 필터를 덮어썼을 때(`didSetFilter`)만 favoriteId 까지 걷어낸다. 두 파라미터가
     * 함께 오면 setFilter 가 자기 몫으로 router.replace 를 한 번 더 부르는데, 같은 틱에서는
     * 서로의 stale query 를 되살려 파라미터가 URL 에 남기 때문이다.
     * 반대로 모르는 slug 라 아무것도 안 했을 때는 favoriteId 를 건드리지 않는다 —
     * 남의 파라미터까지 지우면 즐겨찾기 공유 링크가 무력화된다.
     */
    const removeQueryParam = (didSetFilter: boolean) => {
      const { [APPROVAL_FILTER_QUERY_KEY]: _, ...restQuery } = router.query;
      if (didSetFilter) delete restQuery.favoriteId;
      router.replace(
        { pathname: router.pathname, query: restQuery },
        undefined,
        { shallow: true },
      );
    };

    const filterId = parseApprovalFilterSlug(rawSlug);
    if (filterId === undefined) {
      appliedRef.current = true;
      removeQueryParam(false);
      return;
    }

    // '결재할 문서'처럼 본인이 조건에 들어가는 필터는 내 정보가 채워진 뒤에 걸어야 한다.
    // 하이드레이션 전에 적용하면 passportId 가 빈 값으로 조회된다.
    if (!passportId) return;

    appliedRef.current = true;
    setFilterRef.current(filterId);
    removeQueryParam(true);
  }, [router.isReady, rawSlug, passportId]);
};

export default useApprovalFilterLink;
